import React, { useCallback } from "react";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { useZoom } from "@/contexts/ZoomContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { DropdownMenu } from "@/components/DropdownMenu";
import { AnnotationLabelsToggle } from "@/components/flow/build/AnnotationLabelsToggle";
import { ZoomControls } from "@/components/flow/build/ZoomControls";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { useCurrentMedia, useInferenceQueueItem, InferenceStatus } from "@/store";
import { useCurrentWorkflow } from "@/hooks/useCurrentWorkflow";
import { useFlow } from "@/contexts/FlowContext";
import { useModel } from "@/contexts/ModelContext";
import { useDownloadAnnotatedFile } from "@/hooks/useDownloadAnnotatedFile";
import { useShareWorkflow } from "@/hooks/useShareWorkflow";
import { useSignInAndCreateWorkflow } from "@/hooks/useSignInAndCreateWorkflow";
import { useVisibilityThroughWorkspace } from "@/hooks/useVisibilityThroughWorkspace";
import { useFlowState } from "@/hooks/useFlowState";
import { Flows } from "@/types/flows";
import Button from "@scripts/react/components/Button";

interface AnnotationControlsProps {
    className?: string;
}

type ControlOption = {
    id: string;
    label: string;
    action: () => void;
    isSelected?: boolean;
    icon: string;
    disabled?: boolean;
    tooltip?: string;
};

export const AnnotationControls: React.FC<AnnotationControlsProps> = ({ className = "" }) => {
    const { showLabels, setShowLabels } = useCoreApp();
    const { resetZoom, zoomIn, zoomOut } = useZoom();
    const { flow } = useFlowState();

    // Model-specific functionality (only for DEPLOY flow)
    const isDeployFlow = flow === Flows.DEPLOY;
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const currentMedia = useCurrentMedia();
    const { task } = useFlow();
    const { isDownloading, isTestingNewFiles, downloadProgress } = useModel();
    const inferenceQueueItem = useInferenceQueueItem(currentMedia?.file?.id);
    const isInferenceComplete = inferenceQueueItem?.status === InferenceStatus.COMPLETED;
    const handleDownload = useDownloadAnnotatedFile({ task });
    const { copied, handleShare } = useShareWorkflow();
    const { signInAndCreateWorkflow, loading: signInLoading } = useSignInAndCreateWorkflow();
    const hasWorkflow = !!workflow;
    const downloadDisabled =
        isDownloading || isTestingNewFiles || !isInferenceComplete || signInLoading;

    const handleDownloadClick = useCallback(() => {
        if (hasWorkflow) {
            handleDownload();
        } else {
            signInAndCreateWorkflow();
        }
    }, [hasWorkflow, handleDownload, signInAndCreateWorkflow]);

    const handleShareClick = useCallback(() => {
        if (hasWorkflow) {
            handleShare();
        } else {
            signInAndCreateWorkflow();
        }
    }, [hasWorkflow, handleShare, signInAndCreateWorkflow]);

    const getDownloadButtonTooltip = () => {
        if (isDownloading) {
            return `Downloading... ${Math.round(downloadProgress)}%`;
        }
        if (isTestingNewFiles) {
            return "Testing new files...";
        }
        if (signInLoading) {
            return "Waiting for sign in...";
        }
        if (!isInferenceComplete) {
            return "Waiting for inference to complete...";
        }
        return `Download annotated ${currentMedia?.file?.isVideo ? "video" : "image"}`;
    };

    const visibility = useVisibilityThroughWorkspace();

    const baseOptions: ControlOption[] = [
        { id: "zoom-in", label: "Zoom In", action: zoomIn, icon: "fas fa-plus" },
        { id: "zoom-out", label: "Zoom Out", action: zoomOut, icon: "fas fa-minus" },
        { id: "reset-zoom", label: "Reset Zoom", action: resetZoom, icon: "fas fa-undo" },
        {
            id: "labels",
            label: showLabels ? "Labels Shown" : "Labels Hidden",
            action: () => setShowLabels(!showLabels),
            icon: showLabels ? "fas fa-eye" : "fas fa-eye-slash"
        }
    ];

    const deployOptions: ControlOption[] = [
        ...(visibility === "public"
            ? [
                  {
                      id: "share",
                      label: copied ? "Link Copied!" : "Share Model",
                      action: handleShareClick,
                      icon: copied ? "far fa-check" : "far fa-link",
                      disabled: signInLoading,
                      tooltip: signInLoading ? "Waiting for sign in..." : undefined
                  }
              ]
            : []),
        {
            id: "download",
            label: isDownloading
                ? `Downloading... ${Math.round(downloadProgress)}%`
                : currentMedia?.file?.isVideo
                  ? "Download Video"
                  : "Download Image",
            action: handleDownloadClick,
            icon: isDownloading ? "far fa-spinner fa-spin" : "far fa-download",
            disabled: downloadDisabled,
            tooltip: downloadDisabled ? getDownloadButtonTooltip() : undefined
        }
    ];

    const options: ControlOption[] = isDeployFlow
        ? [...deployOptions, ...baseOptions]
        : baseOptions;

    return (
        <>
            {/* Desktop: Full controls */}
            <div
                className={twClassNames("hidden items-center justify-end gap-2 lg:flex", className)}
            >
                {isDeployFlow && (
                    <>
                        <div className="flex items-center">
                            {visibility === "public" && (
                                <TooltipContainer
                                    type="titleOnly"
                                    title={
                                        signInLoading
                                            ? "Waiting for sign in..."
                                            : "Copy shareable link"
                                    }
                                    customButton={
                                        <span>
                                            <Button
                                                type="ghost"
                                                size="xsmall"
                                                onClick={handleShareClick}
                                                disabled={signInLoading}
                                            >
                                                <i
                                                    className={
                                                        copied ? "far fa-check" : "far fa-link"
                                                    }
                                                />
                                                {copied ? "Copied!" : "Share"}
                                            </Button>
                                        </span>
                                    }
                                />
                            )}
                            <TooltipContainer
                                type="titleOnly"
                                title={getDownloadButtonTooltip()}
                                customButton={
                                    <span>
                                        <Button
                                            type="ghost"
                                            size="xsmall"
                                            shape="square"
                                            onClick={
                                                downloadDisabled ? undefined : handleDownloadClick
                                            }
                                            disabled={downloadDisabled}
                                        >
                                            <i
                                                className={
                                                    isDownloading
                                                        ? "fas fa-spinner fa-spin"
                                                        : "far fa-download"
                                                }
                                            />
                                        </Button>
                                    </span>
                                }
                            />
                        </div>
                        <div className="h-6 w-px flex-shrink-0 bg-divider" />
                    </>
                )}

                <div className="flex items-center">
                    <AnnotationLabelsToggle showLabels={showLabels} onToggle={setShowLabels} />
                    <ZoomControls />
                </div>
            </div>

            <div className={twClassNames("flex items-center justify-end lg:hidden", className)}>
                <DropdownMenu
                    items={options}
                    selectedItem={options[0]}
                    onSelectItem={(item) => item.action()}
                    getItemLabel={(item) => item.label}
                    getLeftContent={(item) => <i className={`${item.icon} fa-fw`} />}
                    getItemKey={(item) => item.id}
                    getItemDisabled={(item) => !!item.disabled}
                    getItemClassName={(item) => (item.disabled ? "opacity-50" : undefined)}
                    getItemTooltip={(item) => item.tooltip}
                    buttonClassName="btn2 ghost xsmall square"
                    customMenuButton={<i className="fas fa-cog"></i>}
                    menuItemsClassName="!w-max right-0 mt-1"
                    stayOpenAfterSelection={true}
                />
            </div>
        </>
    );
};
